import { createSlice } from "@reduxjs/toolkit";
import { fetchUsers, createUser, updateUser, resetUserPassword } from "./userManagementActions";
import { getApiErrorMessage } from "../../utils/commonUtilities";

const fetchUsersSlice = createSlice({
  name: "fetchUsers",
  initialState: {
    isLoadingUsers: false,
    users: {
      users: [],
      totalUsers: 0,
      totalActiveUsers: 0,
      params: {
        pageSize: 10,
        pageNumber: 1
      }
    },
    usersError: null,
    usersErrorMsg: "",
    currentFilters: {
      searchString: "",
      roles: [],
      status: "true",
      pageNumber: 1,
      pageSize: 10
    }
  },
  reducers: {
    updateFilters: (state, action) => {
      state.currentFilters = {
        ...state.currentFilters,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoadingUsers = true;
        state.usersError = null;
        state.usersErrorMsg = "";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        const { data, filters } = action.payload;
        
        if (data.metadata) {
          const status = data.metadata.outcome.status;
          
          if (status === 200) {
            let userPagingDTO = data.data.userPagingDTO || {
              users: [],
              totalUsers: 0,
              totalActiveUsers: 0,
              params: {
                pageSize: 10,
                pageNumber: 1
              }
            };

            if (filters.status !== "all" && userPagingDTO.users.length > 0) {
              const formattedStatus = (filters.status === "enabled" || filters.status === "true") ? "true" : "false";
              const users = userPagingDTO.users;
              
              const needsClientFiltering = users.some(user => user.enabled !== formattedStatus);
              if (needsClientFiltering) {
                const filteredUsers = users.filter(user => user.enabled === formattedStatus);
                
                userPagingDTO = {
                  ...userPagingDTO,
                  users: filteredUsers,
                  totalUsers: filteredUsers.length.toString(),
                  totalActiveUsers: formattedStatus === "true" ? filteredUsers.length.toString() : "0"
                };
              }
            }
            
            state.users = userPagingDTO;
            state.usersError = false;
          } else {
            state.usersError = true;
            state.usersErrorMsg = data.metadata.outcome.message || 
              getApiErrorMessage(status);
          }
        } else {
          state.usersError = true;
          state.usersErrorMsg = "Invalid response format";
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoadingUsers = false;
        state.usersError = true;
        state.usersErrorMsg = action.error.message || "Something went wrong! Please try again later.";
      });
  },
});

const createUserSlice = createSlice({
  name: "createUser",
  initialState: {
    isCreatingUser: false,
    createUserError: null,
    createUserErrorMsg: "",
    createUserSuccess: false,
  },
  reducers: {
    resetCreateUserStatus: (state) => {
      state.createUserSuccess = false;
      state.createUserError = null;
      state.createUserErrorMsg = "";
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUser.pending, (state) => {
        state.isCreatingUser = true;
        state.createUserError = null;
        state.createUserErrorMsg = "";
        state.createUserSuccess = false;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isCreatingUser = false;
        if (!action.payload.success) {
          state.createUserError = true;
          state.createUserErrorMsg = action.payload.metadata?.outcome.message || 
            getApiErrorMessage(action.payload.status);
        } else {
          state.createUserSuccess = true;
          state.createUserError = false;
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isCreatingUser = false;
        state.createUserError = true;
        state.createUserErrorMsg = action.error.message || "Failed to create user. Please try again later.";
      });
  },
});

const updateUserSlice = createSlice({
  name: "updateUser",
  initialState: {
    isUpdatingUser: false,
    updateUserError: null,
    updateUserErrorMsg: "",
    updateUserSuccess: false,
  },
  reducers: {
    resetUpdateUserStatus: (state) => {
      state.updateUserSuccess = false;
      state.updateUserError = null;
      state.updateUserErrorMsg = "";
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateUser.pending, (state) => {
        state.isUpdatingUser = true;
        state.updateUserError = null;
        state.updateUserErrorMsg = "";
        state.updateUserSuccess = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isUpdatingUser = false;
        if (!action.payload.success) {
          state.updateUserError = true;
          state.updateUserErrorMsg = action.payload.metadata?.outcome.message || 
            getApiErrorMessage(action.payload.status);
        } else {
          state.updateUserSuccess = true;
          state.updateUserError = false;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isUpdatingUser = false;
        state.updateUserError = true;
        state.updateUserErrorMsg = action.error.message || "Failed to update user. Please try again later.";
      });
  },
});

const resetPasswordSlice = createSlice({
  name: "resetPassword",
  initialState: {
    isResettingPassword: false,
    resetPasswordError: null,
    resetPasswordErrorMsg: "",
    resetPasswordSuccess: false,
    resetPasswordData: null
  },
  reducers: {
    resetPasswordStatus: (state) => {
      state.resetPasswordSuccess = false;
      state.resetPasswordError = null;
      state.resetPasswordErrorMsg = "";
      state.resetPasswordData = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetUserPassword.pending, (state) => {
        state.isResettingPassword = true;
        state.resetPasswordError = null;
        state.resetPasswordErrorMsg = "";
        state.resetPasswordSuccess = false;
        state.resetPasswordData = null;
      })
      .addCase(resetUserPassword.fulfilled, (state, action) => {
        state.isResettingPassword = false;
        if (!action.payload.success) {
          state.resetPasswordError = true;
          state.resetPasswordErrorMsg = action.payload.metadata?.outcome.message || 
            getApiErrorMessage(action.payload.status);
        } else {
          state.resetPasswordSuccess = true;
          state.resetPasswordError = false;
          state.resetPasswordData = action.payload;
        }
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.isResettingPassword = false;
        state.resetPasswordError = true;
        state.resetPasswordErrorMsg = action.error.message || "Failed to reset password. Please try again later.";
      });
  },
});


export const { updateFilters } = fetchUsersSlice.actions;
export const { resetCreateUserStatus } = createUserSlice.actions;
export const { resetUpdateUserStatus } = updateUserSlice.actions;
export const { resetPasswordStatus } = resetPasswordSlice.actions;


export const fetchUsersReducer = fetchUsersSlice.reducer;
export const createUserReducer = createUserSlice.reducer;
export const updateUserReducer = updateUserSlice.reducer;
export const resetPasswordReducer = resetPasswordSlice.reducer;